import { FC, useEffect, useState, useCallback } from 'react';
import UKDrawer from '@/components/UKDrawer';
import UKProductSelect from './components';
import { observer } from 'mobx-react';
import UKPageLoading from '@/components/UKPageLoading';
import { useTranslation } from '@/i18n/client';
import { TypeDetailInfo } from '@/apis/cart/cart.interface';
import to from 'await-to-js';
import { loadOtherProTypeDetails } from './utils/requestProTypeUtils';
import { AttrUtils, AttrValueVo } from './utils/attrUtils';
import ServiceController from '../product/serviceGuaranteeDrawer';
import { BUTTON_TYPE } from '../product/const';

interface UKProductSelDrawerProps {
  skuId: string;
  handleClose: () => void;
  openFromCart?: boolean;
  btnText?: string;
  btnTextColor?: string;
  updateItemData?: (params: any) => void; // 购买回调
  triggerOpen: boolean;
  isShowService?: boolean; // 是否显示服务
  serviceItemIds?: string[]; // 选择的服务ID列表
  isServiceProAdd?: boolean; // 是否是服务商品添加数量
  isShowPropertyTagMap?: boolean; // 是否有PropertyTagMap，有则打开规格抽屉而非服务抽屉
}

const UKProductSelDrawer: FC<UKProductSelDrawerProps> = observer(
  ({
    skuId,
    handleClose,
    openFromCart = false,
    btnText,
    btnTextColor,
    updateItemData,
    triggerOpen,
    isShowService = false, // 是否显示/存在服务
    serviceItemIds,
    isServiceProAdd = false,
    isShowPropertyTagMap = false,
  }) => {
    const { t } = useTranslation();

    const [isLoading, setIsLoading] = useState(true);
    const [typeDetailInfo, setTypeDetailInfo] = useState<{
      isShowService: boolean;
      info: TypeDetailInfo | null;
    }>();
    const [currentSkuId, setCurrentSkuId] = useState<string>(skuId); // 组件内部的skuId
    // 如果已知商品有规格/非第一次加载，打开抽屉并加载loading，否则加载全局的loading mask
    const [isInitialized, setIsInitialized] = useState(false);
    const [isMuliType, setIsMuliType] = useState(false); // 是否是多规格商品

    // 请求抽屉数据
    const loadData = useCallback(
      async (loadSkuId: string, isFirstLoad: boolean = false) => {
        // !openFromCart && isFirstLoad && UKMaskLoading.show();
        setIsLoading(true);
        const [err, result] = await to(loadOtherProTypeDetails(loadSkuId, serviceItemIds));
        // !openFromCart && isFirstLoad && UKMaskLoading.close();
        setIsLoading(false);
        if (err || !result) {
          if (!isInitialized && !openFromCart) {
            handleClose();
          }
        } else {
          const { isSelect, typeDetailInfo } = result;
          const hasMultipleTypes = isSelect || isShowPropertyTagMap;
          setIsMuliType(hasMultipleTypes);
          // 获取服务模块信息
          const serviceModule = typeDetailInfo?.serviceModule;
          const serviceItems = serviceModule?.serviceItems;
          const hasShowService = serviceItems && serviceItems?.length > 0;
          // 1 通过loadOtherProTypeDetails获取服务或者规格
          // 2 如果是购物车，打开规格弹层（如果有服务，则加上服务组件）
          // 3 如果推荐商卡等类型，则需要判断是否有规格且是否有服务及传入的展示服务
          // 4 如果都有，则打开规格弹层，如果只有规格则打开规格弹层，如果只有服务则打开服务弹层
          if (isServiceProAdd || !openFromCart) {
            if (!hasMultipleTypes && (!isShowService || !hasShowService)) {
              handleClose();
              updateItemData && updateItemData({ skuId: loadSkuId });
              return;
            }
            if (!hasMultipleTypes && isShowService && hasShowService) {
              ServiceController.getInstance().openDrawer({
                mainSkuId: String(loadSkuId),
                title: typeDetailInfo?.serviceModule?.title,
                btnType: BUTTON_TYPE.AddCart,
                serviceData: typeDetailInfo?.serviceModule,
                localInfo: typeDetailInfo?.localInfo,
                serviceItemIds,
                addCart: (params) => {
                  updateItemData &&
                    updateItemData({ skuId: loadSkuId, ...params, isServiceProAdd });
                },
                onClose: handleClose,
              });
              return;
            }
          }
          // 如果有规格，更新抽屉数据
          setTypeDetailInfo({ isShowService, info: typeDetailInfo ?? null });
          if (!openFromCart) {
            setIsInitialized(true);
          }
        }
      },
      [
        openFromCart,
        serviceItemIds,
        isInitialized,
        handleClose,
        isShowPropertyTagMap,
        isShowService,
        updateItemData,
        isServiceProAdd,
      ],
    );

    // 规格切换
    const handleClickType = async (item: AttrValueVo, skuId?: string, currentChecked?: boolean) => {
      if (!typeDetailInfo?.info || !skuId) {
        return;
      }
      item.checked = !currentChecked;
      const attributeCheckResult = AttrUtils.onAttrChecked(
        item,
        typeDetailInfo?.info?.selectedModule?.colorSizePartList || [],
        skuId,
      );
      if (!attributeCheckResult.skuId) {
        return;
      }
      await loadData(attributeCheckResult.skuId, false);
      setCurrentSkuId(attributeCheckResult.skuId);
    };

    // 系列切换
    const onSeriesSelect = async (skus: string[]) => {
      const seriesSkuId = skus?.[0];
      if (seriesSkuId) {
        await loadData(seriesSkuId);
        setCurrentSkuId(seriesSkuId);
      }
    };

    // 初始化加载数据
    useEffect(() => {
      if (triggerOpen) {
        loadData(skuId, true);
        setCurrentSkuId(skuId);
      }
    }, [skuId, loadData, triggerOpen]);

    return (
      <UKDrawer
        width={616}
        openDrawer={triggerOpen && (isInitialized || openFromCart) && isMuliType}
        handleClose={handleClose}
        showTitle={true}
        title={t('cart.page.options')}
      >
        {(openFromCart || isInitialized) && isLoading && (
          <UKPageLoading text={t('common.loading')} />
        )}
        {typeDetailInfo?.info && !isLoading && (
          <UKProductSelect
            skuId={currentSkuId}
            handleClose={handleClose}
            updateItemData={(params) => {
              params.isServiceProAdd = isServiceProAdd;
              updateItemData && updateItemData({ ...params });
            }}
            btnText={btnText}
            btnTextColor={btnTextColor}
            typeDetailInfo={typeDetailInfo.info}
            handleClickType={handleClickType}
            onSeriesSelect={onSeriesSelect}
            isShowService={isShowService}
          />
        )}
      </UKDrawer>
    );
  },
);

export default UKProductSelDrawer;
